import {
  Component, EventEmitter, Input, Output, OnChanges, OnDestroy, SimpleChanges
} from '@angular/core';
import { PowerRound } from '../../../interfaces/power-burst.interface';

interface Orb { x: number; y: number; r: number; delay: number; }
interface Card { x: number; value: number; }

/**
 * Power Burst play surface — a reactor and four answer orbs. Tapping the value
 * of `base^exp` detonates a chain reaction: a seed orb multiplies generation by
 * generation (b → b² → … → bⁿ) into a glowing sunflower burst of exactly
 * `value` orbs. A wrong answer just fizzles that orb.
 */
@Component({
  selector: 'app-power-burst-stage',
  templateUrl: './power-burst-stage.component.html',
  styleUrls: ['./power-burst-stage.component.scss']
})
export class PowerBurstStageComponent implements OnChanges, OnDestroy {
  @Input() round: PowerRound | null = null;
  @Input() roundKey = 0;
  @Input() interactive = true;
  @Input() currLang: any = 'en';

  @Output() solved = new EventEmitter<void>();
  @Output() missed = new EventEmitter<void>();

  readonly VIEW_W = 1000;
  readonly VIEW_H = 560;
  readonly RX = 500;   // reactor centre
  readonly RY = 208;
  readonly CARD_Y = 452;
  private readonly CARD_XS = [185, 395, 605, 815];

  solvedState = false;
  chosen = -1;
  wrong: number[] = [];
  blastOrbs: Orb[] = [];

  private timers: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.reset();
  }
  ngOnDestroy(): void { this.clearTimers(); }

  private reset(): void {
    this.clearTimers();
    this.solvedState = false;
    this.chosen = -1;
    this.wrong = [];
    this.blastOrbs = [];
  }

  get base(): number { return this.round?.base ?? 2; }
  get exp(): number { return this.round?.exp ?? 2; }
  get value(): number { return this.round?.value ?? 0; }

  get cards(): Card[] {
    return (this.round?.options ?? []).map((v, i) => ({ x: this.CARD_XS[i] ?? 500, value: v }));
  }
  isWrong(i: number): boolean { return this.wrong.includes(i); }
  isChosen(i: number): boolean { return this.chosen === i; }

  onCard(i: number): void {
    if (!this.interactive || this.solvedState || this.isWrong(i)) return;
    const v = this.cards[i]?.value;
    if (v === this.value) {
      this.solvedState = true;
      this.chosen = i;
      this.spawnBurst();
      this.solved.emit();
    } else {
      this.wrong.push(i);
      this.missed.emit();
    }
  }

  /** Golden-angle sunflower of `value` orbs; each generation (b, b², …) pops in
   *  a wave, so the burst visibly grows outward exponentially. */
  private spawnBurst(): void {
    const b = this.base, val = this.value;
    const GA = 2.399963229;
    const scale = val > 40 ? 10.5 : val > 20 ? 13.5 : 17;
    const orbR = val > 45 ? 8 : val > 24 ? 10 : 13;
    const lnB = Math.log(b);
    this.blastOrbs = Array.from({ length: val }, (_, i) => {
      const r = scale * Math.sqrt(i + 0.6);
      const a = i * GA;
      const gen = i === 0 ? 1 : Math.floor(Math.log(i) / lnB) + 1;
      return { x: this.RX + r * Math.cos(a), y: this.RY + r * Math.sin(a), r: orbR, delay: gen * 210 };
    });
  }

  private clearTimers(): void { this.timers.forEach(t => clearTimeout(t)); this.timers = []; }
}
